type GtmWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
};

export interface GtmPurchaseItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
}

export interface GtmPurchasePayload {
  transaction_id: string;
  value: number;
  currency?: string;
  items?: GtmPurchaseItem[];
  community_code?: string;
}

export interface GoogleConsentState {
  analytics: boolean;
  advertisement: boolean;
}

function getGtmWindow(): GtmWindow {
  return window as GtmWindow;
}

function getDataLayer(): unknown[] {
  const gtmWindow = getGtmWindow();
  gtmWindow.dataLayer = gtmWindow.dataLayer || [];
  return gtmWindow.dataLayer;
}

function ensureGtag(): void {
  const gtmWindow = getGtmWindow();
  getDataLayer();
  if (typeof gtmWindow.gtag !== 'function') {
    gtmWindow.gtag = (...args: unknown[]) => {
      getDataLayer().push(args);
    };
  }
}

function toConsentValue(granted: boolean): 'granted' | 'denied' {
  return granted ? 'granted' : 'denied';
}

function updateGtagConsent(state: GoogleConsentState): void {
  ensureGtag();
  getGtmWindow().gtag?.('consent', 'update', {
    analytics_storage: toConsentValue(state.analytics),
    ad_storage: toConsentValue(state.advertisement),
    ad_user_data: toConsentValue(state.advertisement),
    ad_personalization: toConsentValue(state.advertisement),
  });
  getDataLayer().push({ event: 'cookie_consent_update' });
}

export function updateGoogleConsent(state: GoogleConsentState): void {
  if (typeof window === 'undefined') {
    return;
  }

  updateGtagConsent(state);
}

export const googleConsentApi = {
  update: updateGoogleConsent,
};

export function pushCommunityCodeToGtm(communityCode: string | null | undefined): void {
  if (typeof window === 'undefined' || !communityCode) {
    return;
  }

  getDataLayer().push({ community_code: communityCode });
}

/** Pushes a GA4 recommended `purchase` event for GTM (paid and free). */
export function pushPurchaseEventToGtm(payload: GtmPurchasePayload): void {
  if (typeof window === 'undefined' || !payload?.transaction_id) {
    return;
  }

  const dataLayer = getDataLayer();
  dataLayer.push({ ecommerce: null });
  dataLayer.push({
    event: 'purchase',
    community_code: payload.community_code,
    ecommerce: {
      transaction_id: payload.transaction_id,
      value: Number(payload.value ?? 0),
      currency: payload.currency ?? 'EUR',
      items: payload.items ?? [],
    },
  });
}
